import React from "react";
import { MdBusiness, MdCheck, MdPhone } from "react-icons/md";
import { centerService } from "services/admin/centerService";
import type { Center, CenterUpdate } from "types/domain";
import SectionHeader from "components/ui/SectionHeader";
import ActionButton from "components/ui/ActionButton";
import { useLanguage } from "context/LanguageContext";

interface FormState {
  name: string;
  code: string;
  address: string;
  phone: string;
}

interface CenterSettingsProps {
  // When provided, load and update the named center instead of the manager's
  // own center. Used by System Admin acting-as-center-owner.
  centerId?: string;
}

const EMPTY: FormState = { name: "", code: "", address: "", phone: "" };

export default function CenterSettings({ centerId }: CenterSettingsProps = {}) {
  const { t } = useLanguage();
  const [center, setCenter] = React.useState<Center | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    const fetcher = centerId
      ? centerService.getById(centerId)
      : centerService.getMyCenter();
    fetcher
      .then((c) => {
        if (!mounted) return;
        setCenter(c);
        setForm({
          name: c.name,
          code: c.code || "",
          address: c.address || "",
          phone: c.phone || "",
        });
      })
      .catch(() => {
        if (mounted) setError("centerSettings.loadError");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [centerId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    const payload: CenterUpdate = {};
    if (form.name !== (center?.name || "")) payload.name = form.name;
    if (form.code !== (center?.code || ""))
      payload.code = form.code || undefined;
    if (form.address !== (center?.address || ""))
      payload.address = form.address || undefined;
    if (form.phone !== (center?.phone || ""))
      payload.phone = form.phone || undefined;

    try {
      const updated = centerId
        ? await centerService.update(centerId, payload)
        : await centerService.updateMyCenter(payload);
      setCenter(updated);
      setSuccess("centerSettings.updateSuccess");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "centerSettings.updateError"
      );
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-line bg-surface px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:border-brand-500 focus:ring-1 focus:ring-brand-500";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
      </div>
    );
  }

  if (!center) {
    return (
      <div className="mt-5 px-4">
        <div className="rounded-2xl border border-line bg-surface p-6 shadow-sm">
          <p className="text-sm text-ink-muted">
            {t(error || "centerSettings.noCenter")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 px-4">
      <div className="rounded-2xl border border-line bg-surface p-6 shadow-sm">
        <SectionHeader
          className="mb-5"
          icon={<MdBusiness className="h-5 w-5" />}
          title={t("centers.create.centerInfo")}
          description={t("centerSettings.subtitle")}
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">
              {t("centers.field.name")} <span className="text-red-500">*</span>
            </label>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">
              {t("centers.field.code")}
            </label>
            <input
              className={inputClass}
              value={form.code}
              onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
              placeholder={t("centers.field.codePlaceholder")}
              maxLength={20}
            />
            <p className="mt-1 text-xs text-ink-subtle">
              {t("centers.field.codeHint")}
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">
              {t("centers.field.address")}
            </label>
            <input
              className={inputClass}
              value={form.address}
              onChange={(e) =>
                setForm((p) => ({ ...p, address: e.target.value }))
              }
              placeholder={t("centers.field.addressInput")}
            />
          </div>

          <div>
            <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-ink">
              <MdPhone className="h-4 w-4 text-ink-subtle" />
              {t("settings.phone")}
            </label>
            <input
              type="tel"
              className={inputClass}
              value={form.phone}
              onChange={(e) =>
                setForm((p) => ({ ...p, phone: e.target.value }))
              }
              placeholder={t("centers.field.phonePlaceholder")}
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {t(error)}
            </div>
          )}

          {success && (
            <div className="rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
              {t(success)}
            </div>
          )}

          <div className="pt-2">
            <ActionButton type="submit" variant="primary" disabled={saving}>
              <MdCheck className="h-4 w-4" />
              {saving ? t("action.saving") : t("settings.save")}
            </ActionButton>
          </div>
        </form>
      </div>
    </div>
  );
}
